import {
  BatchedMesh,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  ConeGeometry,
  DirectionalLight,
  Fog,
  Group,
  HemisphereLight,
  Matrix4,
  Mesh,
  MeshStandardNodeMaterial,
  PlaneGeometry,
  PointLight,
  RepeatWrapping,
  Scene,
  SRGBColorSpace,
  TextureLoader,
  Vector3,
  type Renderer,
  type Texture,
} from 'three/webgpu';
import {
  color,
  float,
  mix,
  mx_noise_float,
  normalWorld,
  positionView,
  positionWorld,
  smoothstep,
  texture,
} from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { MAPS, type Aabb, type MapDef } from '@stillsun/shared';
import {
  queryParam,
  registerWarmPool,
  renderFlags,
  rendererCaps,
  requestLatePrecompile,
  type QualityTier,
} from './renderer.js';
import { buildTerrainMesh, grassDensityAt, terrainBounds, terrainPaletteFor } from './terrain.js';
// region.ts imports `loadTiledTexture`/`triplanarColor` back from here; both modules only touch
// the other's exports inside functions, never at module evaluation, so the cycle is benign.
import { attachRegion, regionReady, regionSidecar, spineWreckReady } from './region.js';
import { createLife, type LifeHandle } from './life.js';
import { createGrass, GRASS_COUNT, type GrassField } from './grass.js';
import { createSky, type SkyHandle } from './sky.js';
import { installSunShadows } from './shadows.js';
import { setStylisedSun, upgradeHullMaterial, upgradeRegolithMaterial } from './materials.js';
// crowd.ts imports nothing from this module, so this edge is one-way and safe.
import { setCrowdSun } from './crowd.js';

const DEG = Math.PI / 180;

/**
 * Ceiling on Spine interior point lights. The budget is a property of the *lighting model*,
 * not of the GPU. Under clustered shading (WebGPU) the shader is invariant to light count, so
 * the art keeps every lamp it asks for. Under three's default forward lighting every light is
 * unrolled into every material's shader, and the shader set has to be rebuilt whenever the
 * light list changes.
 *
 * Measured on this scene (RTX 4080, Chrome), synchronous compile for one fresh variant:
 *
 *              4 lights   8      16      26
 *   WebGPU      1739 ms   2204   2766    3529
 *   WebGL2      1488 ms   2025   2962    4227
 *   clustered     12 ms     11     12      16
 *
 * So the fallback needs a real cap, and it must be a *static* set: `LightsNode`'s cache key
 * hashes each light's id, so any scheme that rotates which lights are visible (nearest-N,
 * re-culled on a timer) mints a fresh variant per combination and compiles it on the main
 * thread — strictly worse than the uncapped scene. Pick a subset once per `setMap`, keep it.
 *
 * Only extract markers are placed from map data today (see `setMap`), so the cap is rarely the
 * binding constraint; it stays because whatever authors lamps on `MapDef` next will hit it.
 *
 * Overridable with `?lights=N` — see `renderFlags` in renderer.js for the other escape hatches.
 */
export const MAX_INTERIOR_LIGHTS_CLUSTERED = { pc: 24, low: 12 } as const;
export const MAX_INTERIOR_LIGHTS_FORWARD = { pc: 12, low: 6 } as const;

/** `?lights=N` (N >= 0) overrides the default. Anything unparseable falls back to it. */
function resolveLightCap(low: boolean): number {
  const raw = queryParam('lights');
  if (raw !== null) {
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  const budget =
    rendererCaps.lighting === 'clustered'
      ? MAX_INTERIOR_LIGHTS_CLUSTERED
      : MAX_INTERIOR_LIGHTS_FORWARD;
  return low ? budget.low : budget.pc;
}

/**
 * The angle the world was art-directed at: a few degrees above the horizon, where sin(4.5°) =
 * 0.078 is all the irradiance the ground gets and shadows run 12.7x the caster's height (a 4 m
 * crate throws ~51 m). The world clock now moves the real key light through a full day; this
 * stays as what the shadow frustum and the regolith glint terms are tuned against, and as the
 * seed handed to `createSky`.
 */
export const SUN_ELEVATION_DEG = 4.5;
export const SUN_AZIMUTH_DEG = 205;

/** Fog range. The *colour* belongs to the world clock's ramp — render/sky.ts owns it. */
const FOG_NEAR = 45;
const FOG_FAR = 270;

/**
 * Design sun irradiance at full key. The world clock scales it: `sun.intensity` is this times
 * `sky.keyIntensity`, which runs 0 at the deep of night to 1 at the top of the day. Deliberately
 * large because the key sits low for most of the cycle — at 4.5° above the horizon the ground
 * only receives `intensity * 0.078`, so a "normal" daylight value of ~3 leaves the sun weaker
 * than the ambient and cast shadows stop reading at all.
 */
const SUN_INTENSITY = 12;
/**
 * Design hemisphere fill at full ambient, scaled by `sky.hemiIntensity` the same way. With the
 * PMREM environment gone this light is the *entire* ambient term — every surface not facing the
 * key gets its light from here and nowhere else — so it can no longer be the small ground-bounce
 * stand-in it was when a sky IBL was carrying the fill.
 */
const HEMI_INTENSITY = 1.1;

export interface SceneHandle {
  scene: Scene;
  /** The dome, the world-clock colour ramp and the fog colour — render/sky.ts. */
  sky: SkyHandle;
  sun: DirectionalLight;
  /**
   * Live reference to `sky.keyDir` — the direction toward whatever is currently the key light
   * (the sun by day, the moon by night). It is the sky's own vector, not a copy, so it is
   * always current; anything that *caches* a direction from it (post's sun-shaft origin, say)
   * has to re-read it after `updateSky`.
   */
  sunDir: Vector3;
  /**
   * Advance the world clock: the sky's uniforms, then the two lights, then every stylised
   * material's sun. Uniform and scalar writes only — nothing here rebuilds a node graph, adds
   * or removes a light, or touches `visible` on one (PERF-BUDGET rule 3). Call it once a frame
   * with a wall-clock instant.
   */
  updateSky(unixMs: number): void;
  /**
   * The instanced grass sheet for the current map, or `null` when the map has no heightfield
   * (grass is scattered on terrain, not on the flat-plane fallback). Rebuilt by `setMap`, so
   * read it through the handle every frame rather than caching it across a map swap.
   *
   * The frame loop owes it one `grass.update(dt, cameraWorldPosition)` — an O(1) uniform write
   * that advances the wind clock. Nothing else moves it.
   */
  readonly grass: GrassField | null;
  /**
   * The map's instanced life layer (render/life.ts): lantern flicker, banner cloth, the carrion
   * flock and the Choir's spatial camp loops. Null for a map with no heightfield, for the same
   * reason `grass` is. Rebuilt by `setMap`, so read it through the handle every frame.
   *
   * The frame loop owes it one `life.update(dt, bodyWorldPosition, sky.keyIntensity)`.
   */
  readonly life: LifeHandle | null;
  /** Rebuilds only the map geometry (ground + boxes + props) for a new map. */
  setMap(map: MapDef): void;
  /**
   * Builds a warm pool for every distinct map the current one has an `exit` to, so the next
   * `precompileScene` compiles that map's pipelines too and a gate crossing — which has no
   * loading screen — does not compile them on screen.
   *
   * `await` it *before* a precompile: `registerWarmPool` only appends to the renderer's list, so
   * it takes effect on the next `runPrecompile` and not on one already awaited, and the promise is
   * the neighbour's region GLB — the pool is not complete until it resolves. Idempotent, and
   * fail-soft per map: a neighbour with no data in `MAPS`, a region GLB that will not load, or a
   * build that throws is logged and skipped — a cold gate is a hitch, never a broken boot.
   */
  warmNeighbours(): Promise<void>;
  /**
   * Fired when the Spine's region GLB (render/region.ts) attaches *after* `setMap` returned —
   * i.e. it was still downloading — so the caller can precompile its materials with the render
   * loop parked. Not fired when it attached inside `setMap`'s own tick (the caller's usual
   * post-`setMap` precompile already covers it).
   */
  onRegionAttached: (() => void) | null;
}

/** Direction *towards* the sun, unit length. */
function computeSunDir(): Vector3 {
  const phi = (90 - SUN_ELEVATION_DEG) * DEG; // polar angle from +Y
  const theta = SUN_AZIMUTH_DEG * DEG;
  return new Vector3().setFromSphericalCoords(1, phi, theta);
}

function groundMaterial(): MeshStandardNodeMaterial {
  const mat = new MeshStandardNodeMaterial({ color: 0x2e2622, roughness: 0.95, metalness: 0 });
  if (!renderFlags.noise) return mat;
  // Subtle TSL regolith variation. Entirely cosmetic — if the node graph fails to
  // build on some backend we keep the plain standard material.
  try {
    // Large drifts (~16 m period) carry the colour so the yard doesn't read as a
    // flat card; a fine octave (~0.7 m) only perturbs roughness, which under a
    // grazing sun is what actually produces visible surface tooth.
    const broad = mx_noise_float(positionWorld.mul(0.06)).mul(0.5).add(0.5);
    const fine = mx_noise_float(positionWorld.mul(1.4)).mul(0.5).add(0.5);
    mat.colorNode = mix(color(0x2e2622), color(0x554639), broad);
    mat.roughnessNode = float(0.78).add(broad.mul(0.1)).add(fine.mul(0.14));
  } catch (err) {
    console.warn('[stillsun] TSL ground shading unavailable, using flat material', err);
  }
  return mat;
}

function aabbMesh(b: Aabb, mat: MeshStandardNodeMaterial | MeshStandardNodeMaterial[]): Mesh {
  const sx = Math.max(0.01, b.max.x - b.min.x);
  const sy = Math.max(0.01, b.max.y - b.min.y);
  const sz = Math.max(0.01, b.max.z - b.min.z);
  const m = new Mesh(new BoxGeometry(sx, sy, sz), mat);
  m.position.set((b.min.x + b.max.x) / 2, (b.min.y + b.max.y) / 2, (b.min.z + b.max.z) / 2);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

// ---------------------------------------------------------------------------------------------
// Static-geometry batching: one `BatchedMesh` per material class.
//
// Every map box used to be its own `Mesh`, so a 127-box map cost 127 render objects in the
// colour pass and the same again in each shadow pass. The first fix welded each material class
// into a single `BufferGeometry` with `mergeGeometries`. This is the second: a `BatchedMesh`
// per class, which is one render object (one pipeline, one bind group, one `RenderObjects.get`)
// exactly like the merged mesh, and beats it on four counts that all matter for a 300-character
// city:
//
//   1. Per-box visibility. `setVisibleAt(instanceId, false)` hides one box with no rebuild — a
//      door, a destructible, a shop shutter — where a merged buffer had to be rewelded.
//   2. Per-box frustum culling. `perObjectFrustumCulled` tests each instance's bounding sphere
//      in `onBeforeRender`; a merged mesh spanned the whole map and was never outside the
//      frustum, so it drew every triangle of a 500 m zone from inside one street.
//   3. Map swaps do not re-upload a giant vertex buffer. The batch's own buffer holds only the
//      *unique* geometry (see below); a new map writes instance matrices into a data texture.
//   4. Geometry instances are shared between repeated boxes.
//
// (3) and (4) come from the same decision: a box's size and position live in its **instance
// matrix**, not in its vertices, so one unit cube serves every box on the map. That is safe here
// for two reasons, both load-bearing. Every material below shades from `positionWorld` —
// triplanar hull/crate, planar-XZ floor, procedural ceiling seams — never from the mesh's own
// 0..1 face UV, so there is no UV scale to preserve. And three's `batch()` node (the WebGPU node
// path, `NodeMaterial.setupPosition` → `batch(object)`) divides `normalLocal` by the squared
// column lengths of the instance matrix before multiplying, i.e. it applies the inverse-scale
// correction, so a 20 x 0.4 x 20 slab instance still shades with a correct normal.
//
// The one wrinkle is slabs, which want three different materials across their six faces (deck
// floor on top, ceiling underneath, hull on the sides). A `Mesh` with a material array draws
// once per group, and `BatchedMesh` has no per-group concept at all, so a slab is split by face
// into the three single-material batches. After `toNonIndexed()` a `BoxGeometry` is 36 vertices
// in the group order the file documents below, so face *f* is the six-vertex run at
// `[f * 6, f * 6 + 6)`.
//
// Cost note, so nobody re-derives it from `renderer.info`: r185's WebGPU backend has no
// multi-draw-indirect path, so it issues one `drawIndexed` per *visible instance* on the pass
// encoder and `WebGPUInfo.update` increments `drawCalls` once per sub-draw. The number in
// `renderer.info.render.drawCalls` is therefore the visible box count, not 1 — but the expensive
// per-draw work (pipeline bind, bind-group set, `RenderObjects.get`'s cache-key rebuild, the
// matrix upload) happens once for the whole batch. Draws are cheap; render objects are not.
// ---------------------------------------------------------------------------------------------

/** `BoxGeometry` face order, per its own group layout. */
const FACE_PX = 0;
const FACE_NX = 1;
const FACE_PY = 2;
const FACE_NY = 3;
const FACE_PZ = 4;
const FACE_NZ = 5;
const BOX_ALL = [FACE_PX, FACE_NX, FACE_PY, FACE_NY, FACE_PZ, FACE_NZ] as const;
const BOX_SIDES = [FACE_PX, FACE_NX, FACE_PZ, FACE_NZ] as const;
const BOX_TOP = [FACE_PY] as const;
const BOX_BOTTOM = [FACE_NY] as const;
/** The attributes `BoxGeometry`/`ConeGeometry` carry, and all `mergeGeometries` needs to agree on. */
const MERGE_ATTRIBUTES = ['position', 'normal', 'uv'] as const;

/** Which faces of the unit cube an instance draws. One `BatchedMesh` geometry slot each. */
export type FaceSet = 'all' | 'sides' | 'top' | 'bottom';
const FACE_SETS: Record<FaceSet, readonly number[]> = {
  all: BOX_ALL,
  sides: BOX_SIDES,
  top: BOX_TOP,
  bottom: BOX_BOTTOM,
};

/** One box's contribution to one material class: which box, and which of its faces. */
interface BoxPart {
  readonly box: Aabb;
  readonly faces: FaceSet;
}

/**
 * The unit cube's face subsets, built once and shared by every batch in the process. These are
 * *staging* geometry: `BatchedMesh.addGeometry` copies them into the batch's own buffer, so they
 * are never uploaded themselves and are kept forever rather than rebuilt per `setMap`. Total
 * cost of the cache is 72 vertices.
 *
 * Bounds are computed here, before `addGeometry`, because `setGeometryAt` clones whatever the
 * source geometry carries into the per-geometry info that `perObjectFrustumCulled` reads.
 * (`computeBoundsTree` is three-mesh-bvh, not r185 core; the core spelling is
 * `computeBoundingBox`/`computeBoundingSphere`.)
 */
const unitCache = new Map<FaceSet, BufferGeometry>();
function unitBox(faces: FaceSet): BufferGeometry {
  const cached = unitCache.get(faces);
  if (cached) return cached;
  const whole = new BoxGeometry(1, 1, 1).toNonIndexed();
  const set = FACE_SETS[faces];
  let geo: BufferGeometry;
  if (set.length === BOX_ALL.length) {
    geo = whole;
  } else {
    geo = new BufferGeometry();
    for (const name of MERGE_ATTRIBUTES) {
      const attr = whole.getAttribute(name);
      if (attr === undefined) continue;
      const item = attr.itemSize;
      const src = attr.array as Float32Array;
      const dst = new Float32Array(set.length * 6 * item);
      let offset = 0;
      for (const f of set) {
        dst.set(src.subarray(f * 6 * item, (f * 6 + 6) * item), offset);
        offset += 6 * item;
      }
      geo.setAttribute(name, new BufferAttribute(dst, item));
    }
    whole.dispose();
  }
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  unitCache.set(faces, geo);
  return geo;
}

/** A `BoxPart` for a box given as a centre and a size, for trim strips that aren't map colliders. */
function partAt(cx: number, cy: number, cz: number, sx: number, sy: number, sz: number): BoxPart {
  const hx = Math.max(0.005, sx / 2);
  const hy = Math.max(0.005, sy / 2);
  const hz = Math.max(0.005, sz / 2);
  return {
    box: {
      min: { x: cx - hx, y: cy - hy, z: cz - hz },
      max: { x: cx + hx, y: cy + hy, z: cz + hz },
    },
    faces: 'all',
  };
}

/** Scratch for `setMatrixAt`; `BatchedMesh` copies out of it immediately. */
const _instanceMatrix = new Matrix4();

/**
 * One `BatchedMesh` per material class. `null` when the class is empty, so an unused class costs
 * no `Object3D`, no draw and no data textures — the same fail-soft contract the merged path had.
 *
 * Sizing: `maxInstanceCount` is the exact part count, and `maxVertexCount` is the sum of the
 * *unique* face subsets this class uses — 66 vertices for a class carrying whole boxes, slab
 * sides and slab undersides, whatever the map's box count. `maxIndexCount` is 0: the unit
 * geometry is non-indexed, so `_initializeGeometry` never allocates an index buffer and the
 * batch's draw ranges are vertex ranges.
 */
function batchedBoxes(
  parts: readonly BoxPart[],
  mat: MeshStandardNodeMaterial,
  cast: boolean,
  receive: boolean,
): BatchedMesh | null {
  if (parts.length === 0) return null;
  const used: FaceSet[] = [];
  for (const p of parts) if (!used.includes(p.faces)) used.push(p.faces);
  let vertexBudget = 0;
  for (const key of used) vertexBudget += unitBox(key).getAttribute('position').count;

  const mesh = new BatchedMesh(parts.length, vertexBudget, 0, mat);
  const geometryIds = new Map<FaceSet, number>();
  for (const key of used) geometryIds.set(key, mesh.addGeometry(unitBox(key)));

  for (const part of parts) {
    const geometryId = geometryIds.get(part.faces);
    if (geometryId === undefined) continue;
    const { min, max } = part.box;
    const instanceId = mesh.addInstance(geometryId);
    // T * S: `makeScale` resets to a pure scale, `setPosition` overwrites the translation column.
    _instanceMatrix.makeScale(
      Math.max(0.01, max.x - min.x),
      Math.max(0.01, max.y - min.y),
      Math.max(0.01, max.z - min.z),
    );
    _instanceMatrix.setPosition((min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2);
    mesh.setMatrixAt(instanceId, _instanceMatrix);
  }

  mesh.castShadow = cast;
  mesh.receiveShadow = receive;
  // Static opaque geometry, and this is CPU we pay per pass (colour plus each cascade, via
  // `onBeforeShadow`). The front-to-back sort allocates and sorts a visible list every one of
  // them to save some overdraw on a world of flat-shaded boxes; the frustum cull removes whole
  // draws for a sphere test each. Keep the second, drop the first.
  mesh.sortObjects = false;
  mesh.perObjectFrustumCulled = true;
  mesh.computeBoundingBox();
  mesh.computeBoundingSphere();
  // `BatchedMesh.dispose()` releases the batch buffer plus its matrix/indirect data textures.
  // Without this a map swap leaks two DataTextures per material class.
  mesh.userData['dispose'] = () => {
    mesh.dispose();
  };
  return mesh;
}

/**
 * Welds `parts` into one mesh and disposes the inputs. `null` when there is nothing to draw —
 * `mergeGeometries` also returns null if the parts ever disagree on their attribute set, in
 * which case dropping the batch is the fail-soft choice (a missing prop beats a thrown build).
 *
 * The one caller left is the horizon wreck ring, and it is the one place where welding still
 * beats a `BatchedMesh`: its silhouettes are uniquely rotated, so they share no geometry, and
 * they encircle the camera, so per-object culling never rejects one. Batching them would trade
 * a single draw for one per spine and buy nothing back.
 */
function mergedMesh(
  parts: readonly BufferGeometry[],
  mat: MeshStandardNodeMaterial,
  cast: boolean,
  receive: boolean,
): Mesh | null {
  if (parts.length === 0) return null;
  const merged: BufferGeometry | null = mergeGeometries([...parts], false);
  for (const p of parts) p.dispose();
  if (merged === null) return null;
  const mesh = new Mesh(merged, mat);
  mesh.castShadow = cast;
  mesh.receiveShadow = receive;
  return mesh;
}

/**
 * Map-generic box classification, replacing v1's fixed roof-slab heights (min.y === 6 || 9,
 * hardcoded to one corridor map). A box is a "slab" (a deck floor, which for every deck but the
 * lowest also doubles as the ceiling of the deck below it — see spine.ts) if it's thin and wide;
 * a "wall" if it's thin and tall; everything else is loose cover (crates, stairs, pillars, ...).
 */
export type BoxKind = 'slab' | 'wall' | 'crate';
export function classifyBox(b: Aabb): BoxKind {
  const sx = b.max.x - b.min.x;
  const sy = b.max.y - b.min.y;
  const sz = b.max.z - b.min.z;
  if (sy <= 1.2 && sx * sz >= 40) return 'slab';
  if (Math.min(sx, sz) <= 0.6 && sy >= 2) return 'wall';
  return 'crate';
}
/** A slab whose min.y is elevated has real headroom underneath it (a deck's floor doubling as
 *  the deck below's ceiling); a slab sitting at/near ground level has nothing under it to light
 *  from below, so it stays plain on its underside instead of getting the ceiling-seam treatment. */
export function isElevatedSlab(b: Aabb): boolean {
  return b.min.y >= 3;
}

/** The four greybox material classes a map's boxes sort into. */
interface KitParts {
  hull: BoxPart[];
  floor: BoxPart[];
  ceiling: BoxPart[];
  crate: BoxPart[];
}

/**
 * Sorts a map's boxes into the four material classes, minus the pit floor (drawn as a void) and
 * minus anything a region/wreck GLB already dresses.
 *
 * Hoisted out of `setMap`'s `batchKit` so the neighbour warm pool (`warmNeighbours` below) sorts
 * a *different* map's boxes through exactly the same rules. Which classes a map populates is
 * what decides its pipeline set — a neighbour with elevated slabs pulls in the ceiling-seam
 * material a flat city never builds — so the two paths agreeing here is the whole point.
 */
function classifyParts(
  boxes: readonly Aabb[],
  pitBox: Aabb | null,
  skip: Set<Aabb> | null,
): KitParts {
  const parts: KitParts = { hull: [], floor: [], ceiling: [], crate: [] };
  for (const box of boxes) {
    if (box === pitBox || skip?.has(box)) continue;
    const kind = classifyBox(box);
    if (kind === 'slab') {
      // BoxGeometry's default 6 groups are ordered [+x, -x, +y, -y, +z, -z]; +y is the top
      // (a deck's own walkable floor) and -y the underside (the deck below's ceiling).
      parts.hull.push({ box, faces: 'sides' });
      parts.floor.push({ box, faces: 'top' });
      (isElevatedSlab(box) ? parts.ceiling : parts.hull).push({ box, faces: 'bottom' });
    } else if (kind === 'wall') {
      parts.hull.push({ box, faces: 'all' });
    } else {
      parts.crate.push({ box, faces: 'all' });
    }
  }
  return parts;
}

/**
 * A shaft's floor has this much open air above it before the next-lowest collider starts. It is
 * the whole discriminator, so it is worth stating why. The naive test — "the lowest box, if it
 * is a wide sheet below the ground plane" — is a *false positive machine* on a zone map, whose
 * terrain dips below `groundY` all over and whose colliders follow it down. Measured on the four
 * maps in the tree: the Spine's drop shaft floor tops out at y -6 with the next collider at
 * -0.4, so 5.6 m of nothing above it; every other candidate (vigil-downs' -2.37..-1.97 slab,
 * lanternfall's -0.4 pad, the Yard's) *overlaps* its neighbours — gap -0.3 m or worse — because
 * it is just low ground, not a hole. 2 m separates those two populations by a factor of three
 * and is also the smallest gap a player could read as a drop rather than as a step.
 */
const PIT_CLEARANCE_M = 2;

/**
 * The map's black-drop pit floor, or `null`. Purely a shape test on the map data — no map id,
 * no coordinate, nothing a new map has to opt out of. A pit floor is the lowest box on the map
 * when it is (a) a wide sheet, (b) sunk below the ground plane, and (c) *isolated*: it stands
 * `PIT_CLEARANCE_M` clear of every other collider, which is what makes it the bottom of a shaft
 * rather than a patch of low ground. (This replaced an `m.id === 'spine'` gate around a much
 * looser test that vigil-downs would have tripped, painting a sixteen-metre stretch of its
 * terrain as an unlit void.)
 */
export function findPitBox(boxes: readonly Aabb[], groundY: number): Aabb | null {
  let lowest: Aabb | null = null;
  for (const b of boxes) {
    if (!lowest || b.min.y < lowest.min.y) lowest = b;
  }
  if (lowest === null) return null;
  if (lowest.min.y >= groundY - 2) return null;
  if ((lowest.max.x - lowest.min.x) * (lowest.max.z - lowest.min.z) < 40) return null;
  // The lowest *other* box: everything must start at least PIT_CLEARANCE_M above this one's top.
  let nextLowest = Infinity;
  for (const b of boxes) {
    if (b !== lowest && b.min.y < nextLowest) nextLowest = b.min.y;
  }
  if (nextLowest === Infinity) return null; // a one-box map has no shaft, only a floor
  return nextLowest - lowest.max.y >= PIT_CLEARANCE_M ? lowest : null;
}

// ---------------------------------------------------------------------------------------------
// Generated textures: loaded once, cached by path, and shared with containers.ts so a locker
// and a map crate never pull down two separate GPU uploads of the same PNG. World-space UVs
// (not the mesh's own 0..1 face UV) so one shared material tiles consistently across boxes of
// any size; `triplanarColor` blends three axis projections by surface normal so it looks right
// on a wall running along x, one running along z, or a horizontal slab, all with the same node.
// ---------------------------------------------------------------------------------------------

const textureCache = new Map<string, Texture>();
/** Loads (or returns the cached) tiled texture at `path`. Shared module-wide, including with
 *  containers.ts, so every material referencing the same asset reuses one GPU texture. */
export function loadTiledTexture(path: string): Texture {
  let tex = textureCache.get(path);
  if (!tex) {
    tex = new TextureLoader().load(path, undefined, undefined, (err) => {
      console.warn(`[stillsun] texture failed to load: ${path}; material stays flat-shaded`, err);
    });
    tex.wrapS = RepeatWrapping;
    tex.wrapT = RepeatWrapping;
    tex.colorSpace = SRGBColorSpace;
    // Every one of these surfaces is seen at a grazing angle: the sun sits at 4.5° and the
    // player's eye is 1.6 m off a deck. `TextureLoader` leaves anisotropy at 1, which picks
    // the mip for the *long* axis of a foreshortened texel footprint — a hull wall seen down
    // its length then collapses to a smear of vertical stripes (the "wooden planks" read on
    // the Spine's outer plating). 16 is the desktop cap; three clamps it to the device max.
    tex.anisotropy = 16;
    textureCache.set(path, tex);
  }
  return tex;
}

/** Triplanar world-space sample of `tex`, tiled every `tileM` metres, blended by |normal|^4 so
 *  the dominant face axis wins with a soft transition at edges/corners. */
const TRIPLANAR_MIP_NEAR = 30;
const TRIPLANAR_MIP_FAR = 120;
const TRIPLANAR_MIP_BIAS = 2;
export function triplanarColor(tex: Texture, tileM: number) {
  const uvX = positionWorld.yz.div(tileM); // projected along +/-X (side walls running north-south)
  const uvY = positionWorld.xz.div(tileM); // projected along +/-Y (floors/roofs)
  const uvZ = positionWorld.xy.div(tileM); // projected along +/-Z (side walls running east-west)
  // Distance mip bias: at a grazing view the plating's rivet rows alias into a dark grid past
  // what anisotropic filtering covers (same failure as the regolith tile — see terrain.ts).
  const farBias = smoothstep(TRIPLANAR_MIP_NEAR, TRIPLANAR_MIP_FAR, positionView.length()).mul(
    TRIPLANAR_MIP_BIAS,
  );
  const cx = texture(tex, uvX).bias(farBias).rgb;
  const cy = texture(tex, uvY).bias(farBias).rgb;
  const cz = texture(tex, uvZ).bias(farBias).rgb;
  const w = normalWorld.abs().pow(4);
  const wsum = w.x.add(w.y).add(w.z).max(1e-5);
  return cx.mul(w.x).add(cy.mul(w.y)).add(cz.mul(w.z)).div(wsum);
}

const HULL_TEXTURE_PATH = '/assets/gen/textures/hull-plating-scorched.png';
const FLOOR_TEXTURE_PATH = '/assets/gen/textures/floor-grating.png';
export const CRATE_TEXTURE_PATH = '/assets/gen/textures/cargo-panel.png';
/**
 * 6 m, not 4. The plating PNG carries five columns of rivets across its width, so at a 4 m
 * tile those columns land 80 cm apart — close enough together, on a 12 m wall seen at a
 * grazing angle, to alias into an even vertical stripe that reads as boarding rather than as
 * a riveted panel. At 6 m the panels are ~1.2 m wide, which is roughly the real thing.
 */
const HULL_TILE_M = 6;
const FLOOR_TILE_M = 2;
const CRATE_TILE_M = 1;

/** Deterministic pseudo-random so the horizon silhouette is stable between reloads. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x100000000;
  };
}

/**
 * A ring of dark wreck spines on the horizon, for depth behind the fog. One merged mesh: they
 * are static, share one material and never cast or receive, so 34 separate meshes bought
 * nothing but 34 draw calls. Each spine's rotation is baked into its vertices — `Euler`'s
 * default XYZ order with no x term is exactly `rotateZ` then `rotateY` applied to the geometry.
 */
function wreckRing(groundY: number, count: number, inner = 150): Mesh | null {
  // Bonelight's shadow anchor (#6F7869), not the old near-black. The point of the ring is depth
  // — shapes dissolving into fog — and under a high-key bone sky a near-black silhouette does not
  // dissolve into anything: it reads as a row of black teeth on the horizon, which is what it did
  // the first time Lanternfall was rendered under the new palette. Fog resolves toward the ridge,
  // so the ring has to sit on the ridge's side of the sky to disappear into it.
  const mat = new MeshStandardNodeMaterial({ color: 0x6f7869, roughness: 1, metalness: 0 });
  const parts: BufferGeometry[] = [];
  const rand = rng(0x1352_9a0b);
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + rand() * 0.09;
    const r = inner + rand() * 70;
    const h = 20 + rand() * 40;
    const w = 3 + rand() * 7;
    const geo: BufferGeometry =
      rand() > 0.45
        ? new BoxGeometry(w, h, w * (0.5 + rand())).toNonIndexed()
        : new ConeGeometry(w * 0.7, h, 5).toNonIndexed();
    geo.rotateZ((rand() - 0.5) * 0.14);
    geo.rotateY(rand() * Math.PI);
    geo.translate(Math.cos(a) * r, groundY + h / 2, Math.sin(a) * r);
    parts.push(geo);
  }
  return mergedMesh(parts, mat, false, false);
}

/** Two crates get amber lantern strips so the greybox reads as inhabited. */
function lanternStrips(boxes: readonly Aabb[]): BoxPart[] {
  const parts: BoxPart[] = [];
  const picks = [0, 3];
  for (const idx of picks) {
    const b = boxes[idx];
    if (!b) continue;
    const sx = b.max.x - b.min.x;
    const sz = b.max.z - b.min.z;
    const y = b.max.y - 0.18;
    const cx = (b.min.x + b.max.x) / 2;
    const cz = (b.min.z + b.max.z) / 2;
    parts.push(partAt(cx, y, b.max.z + 0.03, sx * 0.8, 0.07, 0.05)); // front
    parts.push(partAt(cx, y, b.min.z - 0.03, sx * 0.8, 0.07, 0.05)); // back
    parts.push(partAt(b.min.x - 0.03, y, cz, 0.05, 0.07, sz * 0.8)); // left
    parts.push(partAt(b.max.x + 0.03, y, cz, 0.05, 0.07, sz * 0.8)); // right
  }
  return parts;
}

export function createScene(
  renderer: Renderer,
  map: MapDef,
  tier: QualityTier = 'pc',
): SceneHandle {
  const tScene = performance.now();
  const low = tier === 'low';
  const scene = new Scene();
  // three walks the whole graph in `updateMatrixWorld` at the top of *every* render pass —
  // the colour pass and each shadow cascade — which on a 2,300-object Spine field with twenty
  // Hymns measured 23 ms a frame (four walks) on the owner's 4080, 2026-09-05. The frame loop
  // in main.ts does the one walk itself, right before `post.render()`, and the precompile path
  // (render/renderer.ts) does its own before `compileAsync`.
  scene.matrixWorldAutoUpdate = false;
  // The colour is a placeholder for exactly one frame: `sky.installFog` and then every
  // `updateSky` write `scene.fog.color` from the world clock's ramp. Only near/far live here.
  scene.fog = new Fog(0x000000, FOG_NEAR, FOG_FAR);

  // The angle the world was art-directed at, and still the fallback the shadow frustum and the
  // regolith glints are sized against. The live direction is `sky.keyDir`.
  const designSunDir = computeSunDir();
  // Dome, colour ramp and fog, all keyed off `packages/shared`'s world clock — render/sky.ts.
  const sky = createSky(renderer, { sunDir: designSunDir, tier });
  scene.add(sky.mesh);
  sky.installFog(scene);

  /**
   * How far up the key vector the directional light is parked. `setMap` sizes it to the map (a
   * light inside the map's own bounds clips its own shadow frustum) and `updateSky` re-reads it
   * every frame, so the two cannot fight over `sun.position`.
   */
  let sunDistance = 150;

  const sun = new DirectionalLight(0xffa858, SUN_INTENSITY);
  // Colour and direction both come from the clock's ramp, from the first frame on.
  sun.color.copy(sky.sunColor);
  sun.intensity = SUN_INTENSITY * sky.keyIntensity;
  sun.position.copy(sky.keyDir).multiplyScalar(sunDistance);
  sun.castShadow = true;
  // A 1024 map over the same 90 m ortho box doubles the texel footprint; at a 4.5° sun
  // that costs some contact sharpness, which is a fair trade for a quarter of the
  // shadow-pass fill on a phone.
  sun.shadow.mapSize.set(low ? 1024 : 2048, low ? 1024 : 2048);
  // The ground plane receives but never *casts*, so it is absent from the shadow
  // map and there is no flat-receiver acne to fight. The crate *tops* are still in
  // it though, and they are just as grazing: 60 units of ortho height over 2048
  // texels is 0.029 m of light-up per texel, which on a horizontal face is
  // 0.029 / sin(4.5°) ~= 0.37 m of depth. bias -0.001 over the 399-unit ortho
  // range absorbs ~0.4 m of that. normalBias stays small on purpose: at grazing
  // incidence it walks the lookup many texels along the light's up axis while
  // barely changing the compared depth, i.e. it would shadow the whole yard.
  sun.shadow.bias = -0.001;
  sun.shadow.normalBias = 0.03;
  const sc = sun.shadow.camera;
  // 51 m shadows off a 30 m yard: generous ortho extents, because a clipped shadow
  // vanishes outright whereas an over-large frustum only costs a little sharpness.
  sc.left = -45;
  sc.right = 45;
  sc.top = 30;
  sc.bottom = -30;
  sc.near = 1;
  sc.far = 400;
  sc.updateProjectionMatrix();
  sun.target.position.set(0, 0, 0);
  scene.add(sun);
  scene.add(sun.target);
  // Cascaded shadow maps replace the single 400 m ortho map configured above (which stays as
  // the fallback the cascades clone from). Owns the per-frame frustum tracking — render/shadows.ts.
  installSunShadows(sun, tier);

  // The whole ambient term. There is no PMREM environment any more — `sky.envTexture` is
  // permanently null — so every surface that does not face the key gets its light from these two
  // colours and nothing else. Both come from the clock's ramp, seeded here and moved by
  // `updateSky` on intensity and colour alone; the light itself is never added, removed or
  // hidden, because three hashes the visible light set into every material's cache key.
  const hemi = new HemisphereLight(sky.skyColor, sky.hemiColor, HEMI_INTENSITY * sky.hemiIntensity);
  scene.add(hemi);

  /** Ambient scratch handed to the stylised/crowd sun setters: the hemisphere's own sky/ground
   *  mix, which is exactly the fill a surface facing nowhere in particular actually receives. */
  const ambientMix = new Color();

  /**
   * The regolith upgrade's sun uniform for the current terrain material
   * (`materials.ts` parks it at `mat.userData.sunDirUniform` precisely so a caller that moves
   * the sun can follow). Without this the glints and the opposition surge keep pointing at
   * whatever angle the map was built at while the sky rotates around them.
   */
  let regolithSunUniform: { value: Vector3 } | null = null;
  const trackRegolithSun = (mat: MeshStandardNodeMaterial): void => {
    const u = (mat.userData as { sunDirUniform?: { value: Vector3 } }).sunDirUniform;
    regolithSunUniform = u ?? null;
  };

  const updateSky = (unixMs: number): void => {
    sky.update(unixMs);
    // Key light: colour, intensity and direction. Never `visible`, never a count change.
    sun.color.copy(sky.sunColor);
    sun.intensity = SUN_INTENSITY * sky.keyIntensity;
    sun.position.copy(sky.keyDir).multiplyScalar(sunDistance);
    // Ambient.
    hemi.color.copy(sky.skyColor);
    hemi.groundColor.copy(sky.hemiColor);
    hemi.intensity = HEMI_INTENSITY * sky.hemiIntensity;
    // Everything unlit — props, gear and all three hundred characters — lights against its own
    // sun uniform and would otherwise sit under a placeholder key forever. Three buffer writes.
    ambientMix.copy(sky.skyColor).lerp(sky.hemiColor, 0.5).multiplyScalar(sky.hemiIntensity);
    // `keyColorScaled`, not `sunColor`: this path takes no intensity, so the raw key colour
    // would light every prop and all three hundred characters with a full-strength moon at
    // midnight while terrain and hull — which are on the `DirectionalLight`, and *are* scaled by
    // `keyIntensity` — correctly go dark. See render/sky.ts.
    setStylisedSun(sky.keyDir, sky.keyColorScaled, ambientMix);
    setCrowdSun(sky.keyDir, sky.keyColorScaled, ambientMix);
    if (regolithSunUniform !== null) regolithSunUniform.value.copy(sky.keyDir).normalize();
  };

  // Live tuning handle.
  try {
    (globalThis as { __stillsun?: unknown }).__stillsun = { scene, sun, hemi, sky, renderer };
  } catch {
    /* non-browser host */
  }

  // --- map geometry ---------------------------------------------------------
  const mapRoot = new Group();
  scene.add(mapRoot);

  const groundMat = groundMaterial();
  // Brushed dark steel, not a mirror. At 0.85/0.45 the crates had almost no diffuse
  // term left and could only show whatever the environment probe held, which is why
  // they came out pure black on one face and blown white on the next.
  // Scorched hull plating, triplanar so the one shared material tiles correctly on a wall
  // running along x, one running along z, or a slab's thin edge, all alike. Textures are
  // optional set-dressing: colorNode assignment is wrapped so a failed/missing PNG just
  // leaves the flat brushed-steel colour above (fail soft, per the terrain/ground materials).
  const hullMat = new MeshStandardNodeMaterial({
    color: 0x4e5257,
    metalness: 0.6,
    roughness: 0.55,
  });
  try {
    if (renderFlags.textures) {
      hullMat.colorNode = triplanarColor(loadTiledTexture(HULL_TEXTURE_PATH), HULL_TILE_M);
    }
  } catch (err) {
    console.warn('[stillsun] hull plating texture unavailable, using flat material', err);
  }
  // Procedural edge wear, crevice grime, streaking and micro-normal on top of the plating —
  // render/materials.ts. Fail-soft: leaves the material as built above on any error.
  upgradeHullMaterial(hullMat, { tier });
  // Deck floors — the top face of a "slab" box (see classifyBox). Planar-XZ only: a top face's
  // world normal is always +Y, so the triplanar blend above would be pure overhead and a plain
  // world-space XZ sample is both cheaper and identical in result.
  const floorMat = new MeshStandardNodeMaterial({
    color: 0x3a3f44,
    metalness: 0.5,
    roughness: 0.7,
  });
  try {
    if (renderFlags.textures) {
      floorMat.colorNode = texture(
        loadTiledTexture(FLOOR_TEXTURE_PATH),
        positionWorld.xz.div(FLOOR_TILE_M),
      ).rgb;
    }
  } catch (err) {
    console.warn('[stillsun] floor grating texture unavailable, using flat material', err);
  }
  // The underside of an elevated slab — a deck's floor is the deck below's ceiling. The
  // interiors of the Spine only read as interiors if what is over the player's head is markedly
  // darker than the walls, so this stays dark regardless of texture load; a faint amber seam
  // grid (every ~2 m, matching the floor-grating tile above) hints at the panel joints without
  // a second texture asset. Procedural only — no PNG for this face, so no load path/fail-soft
  // needed beyond the try/catch around the TSL graph itself.
  const ceilingMat = new MeshStandardNodeMaterial({
    color: 0x14171a,
    emissive: new Color(0xffa040),
    emissiveIntensity: 0.15,
    metalness: 0.4,
    roughness: 0.85,
  });
  try {
    // Distance to the nearest 2 m seam line along each axis: fract(coord/2 + 0.5) - 0.5 is 0
    // exactly on a seam and +/-0.5 at the tile midpoint, so abs() folds it into 0..0.5.
    const gx = positionWorld.x.div(2).add(0.5).fract().sub(0.5).abs();
    const gz = positionWorld.z.div(2).add(0.5).fract().sub(0.5).abs();
    const seamMask = float(1)
      .sub(smoothstep(0, 0.05, gx))
      .max(float(1).sub(smoothstep(0, 0.05, gz)));
    ceilingMat.colorNode = color(0x14171a);
    ceilingMat.emissiveNode = color(0xffa040).mul(seamMask).mul(0.4);
  } catch (err) {
    console.warn('[stillsun] ceiling seam shading unavailable, using flat emissive', err);
  }
  // Loose cover — everything classifyBox doesn't call a slab or a wall (crates, stairs,
  // pillars, catwalks). Same triplanar approach as the hull, a tighter 1 m tile to suit
  // smaller props.
  const crateMat = new MeshStandardNodeMaterial({
    color: 0x4a4038,
    metalness: 0.3,
    roughness: 0.6,
  });
  try {
    if (renderFlags.textures) {
      crateMat.colorNode = triplanarColor(loadTiledTexture(CRATE_TEXTURE_PATH), CRATE_TILE_M);
    }
  } catch (err) {
    console.warn('[stillsun] crate panel texture unavailable, using flat material', err);
  }
  // A map's black-drop pit (see `findPitBox`): a flat, unlit void standing in for a fog volume,
  // and a hazard strip around its rim.
  const pitVoidMat = new MeshStandardNodeMaterial({ color: 0x000000, roughness: 1, metalness: 0 });
  // Amber trim: the pit's hazard rim and the crate lantern strips, which were separate
  // materials with identical parameters and so drew as two batches for no reason.
  // (`emissiveIntensity` raised from 4 to hold the same glow after toneMappingExposure
  // 0.9 -> 0.55.)
  const hazardMat = new MeshStandardNodeMaterial({
    color: 0x2a1c10,
    emissive: new Color(0xffa040),
    emissiveIntensity: 6,
    roughness: 0.6,
    metalness: 0,
  });
  /** Bumped per `setMap`, so a region GLB resolving for a map that's already gone is dropped. */
  let mapGeneration = 0;
  let onRegionAttached: (() => void) | null = null;
  let grass: GrassField | null = null;
  let life: LifeHandle | null = null;
  /** Whose `exits` `warmNeighbours` reads. Follows `setMap`. */
  let currentMap: MapDef = map;
  /** Maps whose pipelines this scene has already built — the ones it has drawn, plus the ones it
   *  has a warm pool for. Seeded with the boot map, whose objects are in `mapRoot` already. */
  const warmedMaps = new Set<string>([map.id]);

  const setMap = (m: MapDef): void => {
    const generation = ++mapGeneration;
    currentMap = m;
    warmedMaps.add(m.id);
    for (const child of [...mapRoot.children]) {
      mapRoot.remove(child);
      const dispose = child.userData['dispose'] as (() => void) | undefined;
      if (typeof dispose === 'function') dispose();
    }
    grass = null;
    // The old map's terrain material is gone; drop the uniform `updateSky` was writing into it.
    regolithSunUniform = null;

    // A map with a region GLB draws it when it is available: its colliders (`regionBoxes`) then
    // come out of the greybox batch below, since the GLB carries their visuals. Gated on the map
    // *data* — `regionBoxes` is only ever set by `maps/region.ts` applying a region — rather than
    // on `m.id`, so a new map neither opts in by accident nor needs a change here.
    const drawRegion = renderFlags.region && m.regionBoxes !== undefined;
    const regionBoxSet = drawRegion ? new Set<Aabb>(m.regionBoxes) : null;
    // The wreck GLB dresses every kit box on its own surface, so when it is already loaded the
    // greybox batches below are skipped outright; if it lands late, they are built now and
    // swapped out when it arrives (`kitMeshes`).
    const drawWreck = drawRegion && m.id === 'spine' && spineWreckReady();
    const kitMeshes: BatchedMesh[] = [];
    /** Boxes the wreck GLB already dresses (plus the region's): the greybox skips exactly these,
     *  so a kit box spine.ts grows after the last Blender export still gets a body. */
    const wreckSkip = (): Set<Aabb> =>
      new Set<Aabb>([...(m.regionBoxes ?? []), ...(m.wreckDressedBoxes ?? [])]);

    // A heightfield map builds a displaced terrain mesh (same grid the sim walks in
    // `groundHeight()`, so feet never float or sink relative to what's drawn); maps
    // without one keep the flat plane.
    if (m.world.terrain) {
      const terrain = buildTerrainMesh(m.world.terrain, { low, palette: terrainPaletteFor(m.id) });
      // Sun glints, retro-reflection and near-field grain on top of terrain.ts's regolith —
      // render/materials.ts. Fresh material per `setMap`, so this never stacks.
      if (terrain.material instanceof MeshStandardNodeMaterial) {
        upgradeRegolithMaterial(terrain.material, { tier, sunDir: designSunDir });
        trackRegolithSun(terrain.material);
      }
      // Receive only. The ground is the shadow *receiver*; putting a 100 k-triangle
      // heightfield into the sun's shadow pass as well doubles its cost to produce
      // self-shadowing that a 4.5 degree sun already bakes into the displaced normals.
      terrain.castShadow = false;
      mapRoot.add(terrain);

      // One instanced sheet of blades over the terrain, scattered by the same splat weights the
      // terrain paints itself with (render/terrain.ts owns the formula; this only reads it), so
      // grass grows where the ground is already painted grass and nowhere near rock or sand.
      // One draw, no per-object `Object3D`s, and no per-frame CPU beyond one uniform write.
      const hf = m.world.terrain;
      // The Blender pass knows where the paving, the road and the barrows are; a height/slope
      // rule can only guess. When the sidecar carries a mask it multiplies the density function,
      // so grass grows on the heath and nowhere a cart could roll.
      const grassMask = regionSidecar(m.id)?.grassMask;
      const field = createGrass(hf, {
        count: GRASS_COUNT[tier],
        density: (x, z) => grassDensityAt(hf, x, z, terrainPaletteFor(m.id)),
        tier,
        ...(grassMask && grassMask.length > 0 ? { mask: grassMask } : {}),
      });
      grass = field;
      mapRoot.add(field.mesh);
      // `createGrass` on a *later* `setMap` builds a material the deploy precompile never saw.
      // Coalesced 300 ms in renderer.ts, and a no-op on the first map (whose caller precompiles
      // the whole scene anyway).
      if (mapGeneration > 1) requestLatePrecompile('grass');

      // The life layer. Everything in it is instanced and driven by one time uniform; it adds a
      // single render object (the bird flock) and re-materials the region GLB's own `cloth` and
      // `lantern` meshes in place rather than adding more. Disposed with `mapRoot`, like grass.
      life = createLife({ map: m, heightfield: hf, tier });
      mapRoot.add(life.group);

      if (drawRegion) {
        // The heightfield sheet stands in until the GLB lands (usually already prefetched, so
        // the swap happens before the caller's precompile runs — see `late`).
        const late = !regionReady(m.id);
        const kit = {
          hull: hullMat,
          floor: floorMat,
          ceiling: ceilingMat,
          crate: crateMat,
          hazard: hazardMat,
        };
        void attachRegion(m.id, { low, kit }).then(({ region, wreck }) => {
          const disposeOf = (o: Group | null) => {
            const dispose = o?.userData['dispose'] as (() => void) | undefined;
            if (typeof dispose === 'function') dispose();
          };
          if (generation !== mapGeneration) {
            disposeOf(region);
            disposeOf(wreck);
            return;
          }
          if (region === null) {
            // The GLB failed: keep the sheet, and give the region colliders greybox bodies so
            // nothing in the sim is invisible.
            const parts: BoxPart[] = (m.regionBoxes ?? []).map((b) => ({
              box: b,
              faces: 'all' as const,
            }));
            const fallback = batchedBoxes(parts, crateMat, true, true);
            if (fallback) mapRoot.add(fallback);
          } else {
            mapRoot.remove(terrain);
            terrain.geometry.dispose();
            // The region's terrain gets the same regolith upgrade as the sheet it replaces; its
            // material is freshly built per attach (render/region.ts), so this never stacks.
            // Only where the region's terrain material *is* regolith: Lanternfall's sheet is flat
            // paving and the Downs' is the four-splat heath, and pushing the regolith glint
            // uniforms into either would both look wrong and repoint the uniform `updateSky`
            // writes every frame at a material that never asked for it.
            const regionTerrain = region.getObjectByName('terrain') as Mesh | undefined;
            if (
              terrainPaletteFor(m.id) === 'spine' &&
              m.id === 'spine' &&
              regionTerrain?.material instanceof MeshStandardNodeMaterial
            ) {
              upgradeRegolithMaterial(regionTerrain.material, { tier, sunDir: designSunDir });
              trackRegolithSun(regionTerrain.material);
            }
            mapRoot.add(region);
          }
          if (wreck !== null) {
            // The wreck carries every kit box's visuals: drop the greybox batches (built only
            // when the GLB wasn't ready in time) and put the ship in their place.
            for (const km of kitMeshes) {
              mapRoot.remove(km);
              km.dispose();
            }
            kitMeshes.length = 0;
            for (const km of batchKit(wreckSkip())) {
              mapRoot.add(km);
              kitMeshes.push(km);
            }
            mapRoot.add(wreck);
          }
          // The lantern glass and the banner cloth live inside the GLB's merged meshes, so the
          // life layer can only find them once the GLB is in the scene.
          life?.attachRegion(region);
          life?.attachRegion(wreck);
          if (late) {
            // The GLBs landed after the deploy's precompile: compile their materials with the
            // loop parked (renderer.ts coalesces late requests), rather than on the first frame
            // that draws the wreck.
            requestLatePrecompile('wreck');
            onRegionAttached?.();
          }
        });
      }
    } else {
      const ground = new Mesh(new PlaneGeometry(400, 400, 1, 1), groundMat);
      ground.rotation.x = -Math.PI / 2;
      // The Spine's cargo hold lays a floor slab spanning y -0.4..0, exactly coplanar with
      // groundY. Dropping the global plane 5 mm puts the slab unambiguously on top instead
      // of leaving the two surfaces to flicker against each other across the whole hold.
      ground.position.y = m.world.groundY - 0.005;
      ground.receiveShadow = true;
      mapRoot.add(ground);
    }

    // How far the playable geometry actually reaches, so the shadow frustum, the fog and
    // the horizon ring can all be sized from the map rather than from the yard it was
    // first tuned against. Tracked per axis (not just folded into one `extent`) because the
    // shadow frustum below has to reach a map's *corner*, not just its widest single axis.
    let maxAbsX = 30;
    let maxAbsZ = 30;
    for (const b of m.world.boxes) {
      maxAbsX = Math.max(maxAbsX, Math.abs(b.min.x), Math.abs(b.max.x));
      maxAbsZ = Math.max(maxAbsZ, Math.abs(b.min.z), Math.abs(b.max.z));
    }
    if (m.world.terrain) {
      const tb = terrainBounds(m.world.terrain);
      maxAbsX = Math.max(maxAbsX, Math.abs(tb.minX), Math.abs(tb.maxX));
      maxAbsZ = Math.max(maxAbsZ, Math.abs(tb.minZ), Math.abs(tb.maxZ));
    }
    // Single-axis reach: what the fog distance and the wreck-ring radius were already tuned
    // against, so both keep using this rather than the corner distance below.
    const extent = Math.max(maxAbsX, maxAbsZ);

    // A map's black-drop pit floor, found from the geometry rather than from a map id or a
    // hardcoded coordinate: the lowest box on the map, if it is a wide sheet sunk well below
    // the ground plane. It is excluded from the classifyBox pass below and rendered as the pit
    // void instead, so it never gets a walkable-deck-floor texture it isn't. A map without such
    // a box simply has no pit — no code change here either way.
    const pitBox = findPitBox(m.world.boxes, m.world.groundY);

    // One batch per material class, not one mesh per box. `classifyBox` still runs per box — the
    // classification just decides which batch a box's faces are instanced into. A function
    // (hoisted) because the late-wreck path above rebuilds the batches with a different skip set.
    function batchKit(skip: Set<Aabb> | null): BatchedMesh[] {
      const parts = classifyParts(m.world.boxes, pitBox, skip);
      const out: BatchedMesh[] = [];
      for (const batch of [
        batchedBoxes(parts.hull, hullMat, true, true),
        batchedBoxes(parts.floor, floorMat, true, true),
        batchedBoxes(parts.ceiling, ceilingMat, true, true),
        batchedBoxes(parts.crate, crateMat, true, true),
      ]) {
        if (batch !== null) out.push(batch);
      }
      return out;
    }
    // Greybox amber trim: two crates get lantern strips so a *greybox* reads as inhabited. A map
    // with a region GLB has real modelled lanterns in it (the sidecar's `lanterns` list, flickered
    // by render/life.ts), and running this over its colliders instead painted a 40 m amber line
    // along Lanternfall's wall — the greybox's furniture surviving into the dressed city.
    const trimParts: BoxPart[] = drawWreck || drawRegion ? [] : lanternStrips(m.world.boxes);

    if (pitBox) {
      // An amber hazard strip framing the opening's rim, flush with the floor the hole is cut
      // into — the pit box's XZ footprint is exactly that hole, and the surrounding deck sits at
      // the map's own ground plane. Batched with the lantern strips: same material, same
      // never-casts.
      const rimY = m.world.groundY + 0.03;
      const stripW = 0.15;
      const stripH = 0.04;
      const x0 = pitBox.min.x;
      const x1 = pitBox.max.x;
      const z0 = pitBox.min.z;
      const z1 = pitBox.max.z;
      const spanX = x1 - x0 + stripW;
      const spanZ = z1 - z0 + stripW;
      const cx = (x0 + x1) / 2;
      const cz = (z0 + z1) / 2;
      trimParts.push(partAt(cx, rimY, z0, spanX, stripH, stripW)); // north
      trimParts.push(partAt(cx, rimY, z1, spanX, stripH, stripW)); // south
      trimParts.push(partAt(x0, rimY, cz, stripW, stripH, spanZ)); // west
      trimParts.push(partAt(x1, rimY, cz, stripW, stripH, spanZ)); // east
    }

    for (const batch of batchKit(drawWreck ? wreckSkip() : regionBoxSet)) {
      mapRoot.add(batch);
      kitMeshes.push(batch); // replaceable by a late wreck GLB
    }
    const trimBatch = batchedBoxes(trimParts, hazardMat, false, false);
    if (trimBatch !== null) mapRoot.add(trimBatch);
    // Horizon silhouettes. Still `mergeGeometries`, deliberately: 34 uniquely rotated spines
    // share no geometry, and they ring the camera so per-object culling never rejects one — a
    // batch would trade one draw for 34 and buy nothing.
    //
    // Not in the city. Lanternfall is 120 m of walled square: the ring's inner radius lands it
    // just outside those walls, where it reads as a row of monoliths leaning over the plaza
    // rather than as a distant horizon. Everywhere else it is the far country, and the warm pool
    // below still builds one spine so the pipeline exists whichever map the player lands on.
    const ring =
      m.id === 'lanternfall' ? null : wreckRing(m.world.groundY, low ? 18 : 34, extent + 60);
    if (ring !== null) mapRoot.add(ring);

    if (pitBox) {
      // The pit floor itself, excluded from the classifyBox pass above, drawn as a solid black,
      // unlit void standing in for a fog volume — a full box (not just a plane on top) so its
      // sides stay solid and it never reads as a floating rectangle over open space.
      const voidMesh = aabbMesh(pitBox, pitVoidMat);
      voidMesh.receiveShadow = false;
      voidMesh.castShadow = false;
      mapRoot.add(voidMesh);
    }

    // Interior point lights, placed from the map's own data: one warm marker over each extract,
    // capped by the lighting model's budget. The Spine's per-deck fill grid and Choir-Deck
    // accents used to be placed from mirrored deck-base constants here; they are gone with the
    // rest of the `m.id === 'spine'` branches, because a fixed grid of magic coordinates cannot
    // follow a map it was not written for. Whatever replaces it has to be authored on `MapDef`
    // (a `lamps?: LampDef[]`), which is a `packages/shared` change and not this pass's to make.
    for (const ex of (m.extracts ?? []).slice(0, resolveLightCap(low))) {
      const lamp = new PointLight(0xffa040, 45, 22, 2);
      lamp.position.set(ex.pos.x, ex.pos.y + 2, ex.pos.z);
      lamp.castShadow = false;
      mapRoot.add(lamp);
    }

    // A shadow frustum tuned to a 30 m yard clips everything past it, and a clipped
    // shadow does not soften — it vanishes. Cover the whole map instead and accept the
    // softer contact: at a 4.5 degree sun the penumbra is enormous either way. Sized off
    // the map's *corner* distance, not a single axis: sc.left/right/top/bottom are a square
    // box around the shadow camera's view direction, and a box that only reaches the far
    // edge along X or Z still clips the far corner on the diagonal (a 260 x 160 m map's
    // corner sits ~153 m out even though neither axis alone reaches 150 m). Raised the cap
    // from 120 m to 200 m alongside it so that corner reach actually fits.
    const cornerReach = Math.hypot(maxAbsX, maxAbsZ);
    const half = Math.min(cornerReach + 15, 200);
    sc.left = -half;
    sc.right = half;
    sc.top = half * 0.75;
    sc.bottom = -half * 0.75;
    sc.far = Math.max(400, half * 4);
    sc.updateProjectionMatrix();
    // Written here and re-read by `updateSky` every frame, so the per-map distance survives
    // the clock moving the light. A light parked inside the map's own bounds clips its shadows.
    sunDistance = Math.max(150, half * 1.6);
    sun.position.copy(sky.keyDir).multiplyScalar(sunDistance);

    // A corner reach past 150 m spreads the same shadow-map texel budget thin enough that
    // contact shadows go soft-to-invisible; double the resolution on PC tier only — the low
    // tier stays at its fixed 1024 regardless of map size, the same conservative call as the
    // pixel-ratio and wreck-ring counts above.
    if (!low) {
      const mapSize = cornerReach > 150 ? 4096 : 2048;
      sun.shadow.mapSize.set(mapSize, mapSize);
    }

    // Fog range is not set here any more: sight distance is part of the world clock's ramp
    // (60 m at midnight against 180 m at noon) and `render/sky.ts`'s `updateSky` writes
    // `scene.fog.near`/`.far` through uniform references every frame. Anything written here
    // would be overwritten on the next frame; nothing else in the client reads the range.
  };

  // ------------------------------------------------------------------------------------
  // Neighbour warm pools.
  //
  // `lanternfall` and `vigil-downs` are two rooms in one process: crossing a gate sends
  // `S2C.Transfer`, `setMap` swaps the world, and there is no loading screen to hide a compile
  // behind — so whatever pipelines the *other* map needs used to be built on screen, mid-stride.
  //
  // The obvious fix does not work and the measurement is in main.ts: swapping the other map in
  // behind the loading plate and calling `precompileScene` compiles almost nothing, because
  // `compileAsync` builds its render list through `_projectObject`, which tests
  // `!object.frustumCulled || frustum.intersectsObject(object)` — and the second world sits two
  // hundred metres from the boot camera. Opting *out* of that test is the whole trick, and it is
  // also why the pool's position is irrelevant: `_projectObject` never looks at where an unculled
  // object is, and `renderObject` calls `_handleObjectFunction` (the pipeline build) before any
  // per-instance culling a `BatchedMesh` might do. The 0.001 scale at the origin is only so that
  // the one real `pipeline.render()` inside `post.compile()` draws a speck instead of a city.
  //
  // What goes in: **whatever `setMap` would attach for that map**, built through the same calls —
  // terrain and its grass or the flat ground plane, the life layer, the region/wreck GLBs through
  // `attachRegion`, the greybox classes the map's boxes actually populate, hazard trim, pit void,
  // a wreck-ring spine. Shared materials come from this closure, so a class both maps use costs
  // the pool an object and no new pipeline. Walking the same calls rather than an enumerated list
  // is the load-bearing part: the first cut of this pool listed "terrain + grass + four batches +
  // trim + pit + ring" and skipped the region GLB, and the moment a content pass gave both
  // persistent maps a region, five prop families (`dungeon`, `cloth`, `nature`, `water` and the
  // GLB's own `terrain`) went straight back to compiling at the gate. A pool that enumerates goes
  // stale every time the world grows; one that calls what `setMap` calls does not.
  //
  // **A warm object has to be built the way the real one will be**, and a fresh *material
  // instance* is not the thing that matters — the pipeline cache is keyed on the generated shader
  // string, so the terrain's own freshly-built `MeshStandardNodeMaterial` warms the real one
  // fine. What does matter is anything that changes codegen: a warm grass field built with one
  // blade compiled a *different* vertex program from the real twenty-thousand-blade one and
  // warmed nothing (measured at the gate, 2026-09-06). Hence `WARM_GRASS_BLADES` below.
  // Measured walking the real gate into the Downs, headless: 46 pipelines before the crossing and
  // 45 after, with nothing named by a hook on `Pipelines.getForRender` and no vertex or fragment
  // program compiled, and a worst frame of 10.4 ms. Against 44 -> 47, eight material families
  // named and a 227 ms frame on the same tree with the region left out of the pool.
  //
  // The pool is registered with `registerWarmPool`, so it is attached to the scene only for the
  // duration of each precompile and is not in the frame's traversal in between (WEBGPU-NOTES §6).
  // ------------------------------------------------------------------------------------

  /** Parked scale, matching the other warm pools in the tree. */
  const WARM_SCALE = 0.001;
  /**
   * Blades in a warm grass field: the real budget, not a token one.
   *
   * A single blade compiles the same *node graph* — nothing in `createGrass`'s TSL reads the
   * instance count — but three keys a render object's node-builder state on
   * `object.uuid` for any `InstancedMesh` and regenerates the shader per object, and the
   * measured result was a grass vertex program that missed the cache at the gate anyway
   * (headless probe, 2026-09-06). Matching every input `setMap` will pass is the only lever this
   * file has; the cost is one rejection-sampling pass at boot and ~0.5 MB of instance buffers.
   */
  const WARM_GRASS_BLADES = GRASS_COUNT[tier];

  const buildWarmPool = async (m: MapDef): Promise<Group> => {
    const pool = new Group();
    pool.name = `warm:${m.id}`;
    let life: LifeHandle | null = null;
    if (m.world.terrain) {
      // The neighbour's OWN palette: `downsMaterial` is a pipeline-distinct material, so warming
      // the Downs' sheet with the Spine's regolith rule warms the wrong pipeline and the real one
      // compiles at the gate, on screen, with no loading plate to hide it (WEBGPU-NOTES §8).
      const terrain = buildTerrainMesh(m.world.terrain, { low, palette: terrainPaletteFor(m.id) });
      // The upgrade, for the pipeline — but deliberately *not* `trackRegolithSun`: that repoints
      // the uniform `updateSky` writes every frame, and pointing it at a throwaway copy would
      // freeze the real terrain's sun at whatever angle it was built with.
      if (terrain.material instanceof MeshStandardNodeMaterial) {
        upgradeRegolithMaterial(terrain.material, { tier, sunDir: designSunDir });
      }
      terrain.castShadow = false;
      pool.add(terrain);
      const hf = m.world.terrain;
      pool.add(
        createGrass(hf, {
          count: WARM_GRASS_BLADES,
          density: (x, z) => grassDensityAt(hf, x, z, terrainPaletteFor(m.id)),
          tier,
        }).mesh,
      );
      // The life layer, exactly as `setMap` builds it. It owns one instanced mesh of its own
      // (`life:birds`) and re-materials the region GLB's `cloth` and `lantern` meshes below, so
      // it has to be here *and* be handed the warm region. Nothing drives it — `life.update` is
      // only ever called for the map the player is actually on — so it is inert geometry.
      life = createLife({ map: m, heightfield: hf, tier });
      pool.add(life.group);
    } else {
      const ground = new Mesh(new PlaneGeometry(1, 1, 1, 1), groundMat);
      ground.rotation.x = -Math.PI / 2;
      ground.receiveShadow = true;
      pool.add(ground);
    }

    // The region (and, on the Spine, the wreck) through the same `attachRegion` call `setMap`
    // makes, with the same kit. This is the difference between a pool that warms what a map needs
    // and a pool that warms a list somebody wrote down once: the GLB carries `dungeon`, `cloth`,
    // `nature`, `water`, `lantern` and its own `terrain` mesh today and will carry more later, and
    // none of them has to be named here. Geometry is shared with the loader's template
    // (`clone(true)`), so the pool's copy costs materials and matrices, not a second upload.
    // Awaited, unlike `setMap`'s fire-and-forget attach, because the caller has to be able to get
    // this into the scene before the boot precompile walks it.
    if (renderFlags.region && m.regionBoxes !== undefined) {
      try {
        const kit = {
          hull: hullMat,
          floor: floorMat,
          ceiling: ceilingMat,
          crate: crateMat,
          hazard: hazardMat,
        };
        const { region, wreck } = await attachRegion(m.id, { low, kit });
        if (region !== null) pool.add(region);
        if (wreck !== null) pool.add(wreck);
        // The `cloth`/`lantern` variants only exist once the layer has seen the GLB.
        life?.attachRegion(region);
        life?.attachRegion(wreck);
      } catch (err) {
        console.warn(
          `[stillsun] region GLB unavailable for warm pool '${m.id}'; its props compile at the gate`,
          err,
        );
      }
    }

    const pitBox = findPitBox(m.world.boxes, m.world.groundY);
    // Exactly `setMap`'s `drawRegion` rule, `renderFlags.region` included: under `?region=0` the
    // real map greyboxes its region colliders, so the warm pool has to greybox them too.
    const skip =
      renderFlags.region && m.regionBoxes !== undefined ? new Set<Aabb>(m.regionBoxes) : null;
    const parts = classifyParts(m.world.boxes, pitBox, skip);
    for (const [boxParts, mat] of [
      [parts.hull, hullMat],
      [parts.floor, floorMat],
      [parts.ceiling, ceilingMat],
      [parts.crate, crateMat],
    ] as const) {
      const batch = batchedBoxes(boxParts, mat, true, true);
      if (batch !== null) pool.add(batch);
    }
    // `setMap` skips the greybox lantern strips entirely on a region map (the GLB dresses it), so
    // the pool has to skip them on the same test or it warms a batch the map never draws.
    const drawRegion = renderFlags.region && m.regionBoxes !== undefined;
    const trim = drawRegion
      ? null
      : batchedBoxes(lanternStrips(m.world.boxes), hazardMat, false, false);
    if (trim !== null) pool.add(trim);
    if (pitBox !== null) {
      const voidMesh = aabbMesh(pitBox, pitVoidMat);
      voidMesh.castShadow = false;
      voidMesh.receiveShadow = false;
      pool.add(voidMesh);
    }
    // One spine, not thirty-four: the ring is a merged mesh with one material, so a single
    // silhouette compiles the same pipeline the whole horizon does.
    const ring = wreckRing(m.world.groundY, 1);
    if (ring !== null) pool.add(ring);

    pool.scale.setScalar(WARM_SCALE);
    // The part the naive attempt missed, and it has to be per *object* — `_projectObject` tests
    // the flag on the mesh it is about to push, never on an ancestor Group.
    pool.traverse((o) => {
      o.frustumCulled = false;
      // A `BatchedMesh` culls its own instances in `onBeforeRender`. That cannot stop a pipeline
      // build — `renderObject` calls `_handleObjectFunction` after `onBeforeRender` and before any
      // draw — but there is no reason to leave the warm walk depending on that ordering.
      if (o instanceof BatchedMesh) o.perObjectFrustumCulled = false;
    });
    return pool;
  };

  const warmNeighbours = async (): Promise<void> => {
    for (const exit of currentMap.exits ?? []) {
      if (warmedMaps.has(exit.to)) continue;
      const neighbour = MAPS[exit.to];
      if (neighbour === undefined) {
        console.info(`[stillsun] no map data for '${exit.to}'; that gate compiles on the crossing`);
        continue;
      }
      // Marked warmed either way: a map that threw once will throw again, and a cold gate is a
      // hitch where a rethrown build would be a broken boot.
      warmedMaps.add(exit.to);
      try {
        const t = performance.now();
        const pool = await buildWarmPool(neighbour);
        registerWarmPool(scene, pool);
        let meshes = 0;
        pool.traverse((o) => {
          if ((o as Mesh).isMesh) meshes++;
        });
        console.info(
          `[stillsun] warm pool for neighbouring map '${exit.to}': ` +
            `${String(pool.children.length)} roots, ${String(meshes)} meshes, ` +
            `${String(Math.round(performance.now() - t))} ms`,
        );
      } catch (err) {
        console.warn(
          `[stillsun] could not warm '${exit.to}'; its gate will compile on the crossing`,
          err,
        );
      }
    }
  };

  const tSetMap = performance.now();
  setMap(map);
  const setMapMs = performance.now() - tSetMap;

  // Seed the whole lighting rig from the world clock before anything renders or precompiles, so
  // the first frame is already the right time of day and the stylised/crowd materials are never
  // compiled and shown against their placeholder sun.
  updateSky(Date.now());

  // Phase breakdown for the boot stall investigation. `createScene` builds node graphs but does
  // not compile them: a small number here with a long gap before the first frame is the shader
  // compile, not this.
  console.info(
    `[stillsun] scene built in ${(performance.now() - tScene).toFixed(1)} ms ` +
      `(setMap ${setMapMs.toFixed(1)} ms) · ` +
      `flags tex=${String(renderFlags.textures)} noise=${String(renderFlags.noise)} ` +
      `lights=${String(resolveLightCap(low))} (${rendererCaps.lighting})`,
  );

  return {
    scene,
    sky,
    sun,
    sunDir: sky.keyDir,
    updateSky,
    setMap,
    warmNeighbours,
    get grass() {
      return grass;
    },
    get life() {
      return life;
    },
    get onRegionAttached() {
      return onRegionAttached;
    },
    set onRegionAttached(fn) {
      onRegionAttached = fn;
    },
  };
}
